import { useState, useEffect, useRef } from "react";
import { Slide, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../themes/Default/components/List";
import { Header as ListHeader } from "../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../themes/Default/components/List/components/Body";
import { TableHead } from "../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { employeeWeeklyReportManagement } from "../../hooks/api/EmployeeWeeklyReportManagement";
import { useTranslation } from "react-i18next";
import { useUserToken } from "../../hooks/core/UserToken";
import { roles } from "../../services/utils/Roles";
import { Menu as ListMenu } from "../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Formik } from "formik";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../services/core/Select";
import { employeesManagement } from "../../hooks/api/EmployeesManagement";
import { getNumberOfUsedFilters } from "../../services/utils/Filter";
import { usePersistentFilters } from "../../hooks/core/PersistentFilters";

const WeeklyReports = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [reports, setReports] = useState();
    const { useEmployeeWeeklyReportListMutation } = employeeWeeklyReportManagement;
    const [reportsList] = useEmployeeWeeklyReportListMutation();
    const [totalRecords, setTotalRecords] = useState(0);
    const [{ getDecoded }] = useUserToken(); 
    const decodedToken = getDecoded(); 
    const { useEmployeesListMutation } = employeesManagement;
    const [professorList] = useEmployeesListMutation();
    const [professors, setProfessors] = useState([]);
    const recordsPerPage = 100;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const formRef = useRef(null);
    const [filters, setFilters, resetFilters] = usePersistentFilters("weeklyReportsFilters", defaultFilters);
    const [showFilter, setShowFilter] = useState(false);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            startPage: 0,
            employee: values?.employee,
            startDate: values?.startDate,
        });
    };

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/reports/add");
    };

    const gotoEdit = (employeeWeeklyReport) => {
        navigate("/reports/edit/" + employeeWeeklyReport);
    };

    const gotoView = (employeeWeeklyReport) => {
        navigate("/reports/view/" + employeeWeeklyReport);
    };

    useEffect(() => {
        console.log(filters);

        reportsList(filters).then((result) => {
            const reports = result?.data?.records;
            setReports(reports);
            setTotalRecords(result?.data?.totalRecords);
        });

        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));

        professorList({role: 'ROLE_PROFESSOR'}).then((result) => {
            let options = [];
            result?.data?.records.map((professor) => {
                options.push(
                    {
                        id: professor.id,
                        name: professor.firstName + ' ' + professor.lastName
                    }
                );
            })
            setProfessors(prepareSelect(options));
        });
    }, [filters]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                {(decodedToken?.roles.includes(roles.PROFESSOR) || decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR)) && (
                    <>
                        <Button title={t("report.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                    </>
                )}
            </TopNavigation>
            <Main>
                <Title>{`${t("report.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("report.title")} icon="fa-light fa-clipboard" />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                    <List>
                    <ListHeader>
                            <ListMenu>
                                <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                            </ListMenu>
                            <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={resetFilters}>
                                <Formik
                                    innerRef={formRef}
                                    initialValues={{
                                        employee: filters?.employee,
                                        startDate: filters?.startDate,
                                    }}
                                    onSubmit={onFiltered}
                                >
                                    {({ values, handleChange, setFieldValue }) => (
                                        <Form>
                                            <Row>
                                                <Field>
                                                    <Select 
                                                        name="employee" 
                                                        label={t('groups.professor')} 
                                                        onChange={handleChange} 
                                                        options={professors} 
                                                        selectedValue={values.professor} 
                                                        searchInDropdown={false}
                                                    />
                                                </Field>
                                            </Row>
                                            <Row>
                                                <Field>
                                                    <DatePicker
                                                        name="startDate"
                                                        label={t("report.startDate")}
                                                        value={values.startDate ?? ""}
                                                        onChange={async (e) => {
                                                            const value = e.target.value;
                                                            setFieldValue("startDate", value);
                                                        }} 
                                                        disabledWeekdays={[1,2,3,4,5,6]}
                                                    />
                                                </Field>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            </ListFilter>
                        </ListHeader>
                        <ListBody>
                            <TableHead>
                                <TableRow>
                                    <TableHeadCell></TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("groups.fullName")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("report.period")}</span>
                                    </TableHeadCell>
                                    {(decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.DIRECTOR) || decodedToken?.roles.includes(roles.SUPER_ADMIN)) && (
                                        <>
                                            <TableHeadCell>
                                                <span>{t("Zatražen odmor")}</span>
                                            </TableHeadCell>
                                        </>
                                    )}
                                    <TableHeadCell>
                                        <span>{t("Verifikovao/la")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reports?.length > 0 &&
                                    reports?.map((report, index) => {
                                        return (
                                            <TableRow key={report.id}>
                                                <TableDataCell>
                                                    {report.isSent ? (
                                                        <Button
                                                            icon="fa-regular fa-eye"
                                                            onClick={() => gotoView(report.id)}
                                                        ></Button>
                                                    ) : (
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(report.id)}></Button>
                                                    )}
                                                </TableDataCell>
                                                <TableDataCell>{report.employeeName}</TableDataCell>
                                                <TableDataCell>{report.period}</TableDataCell>
                                                {(decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.DIRECTOR) || decodedToken?.roles.includes(roles.SUPER_ADMIN)) && (
                                                    <>
                                                        <TableDataCell>
                                                            <span style={{ color: report.leaveRequestNote ? '#27ae60' : '#ff0000' }}>
                                                                {report.leaveRequestNote ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}
                                                            </span>
                                                        </TableDataCell>
                                                    </>
                                                )}
                                                <TableDataCell>{report.verifiedBy}</TableDataCell>
                                                <TableDataCell></TableDataCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </ListBody>
                    </List>
            </Main>
        </DefaultLayout>
    );
};

export default WeeklyReports;
